import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { InternalPricingCard } from '../components/InternalPricingCard';
import { toast } from 'sonner';
import {
  ArrowLeft, Send, Download, MoreVertical, MessageSquare, CreditCard,
  ChevronDown, ChevronRight, Sparkles, ExternalLink, X, Loader2, Copy,
  DollarSign, User as UserIcon, Mail, Phone, Check, PenLine,
} from 'lucide-react';
import { AuthContext } from '../App';
import AdminPaymentModal from '../components/AdminPaymentModal';
import PaymentTransactions from '../components/PaymentTransactions';
import PaymentPermissionsPanel from '../components/PaymentPermissionsPanel';
import ShippingPanel from '../components/ShippingPanel';
import EmailActivityPanel from '../components/EmailActivityPanel';
import EditProductModal from '../components/EditProductModal';
import ProductPickerModal from '../components/ProductPickerModal';
import ProductDesignerModal from '../components/ProductDesignerModal';
import PickupSignatureModal from '../components/PickupSignatureModal';
import { unwrapImg } from '../lib/imgUrl';
import { brandName, brandEmail, brandPhone, brandLogo, brandWebsite } from '../lib/brand';

const fmt = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STATUS_STYLES = {
  draft: { bg: '#EFF6FF', text: '#1D4ED8', label: 'DRAFT' },
  sent: { bg: '#FEF3C7', text: '#B45309', label: 'SENT' },
  approved: { bg: '#DCFCE7', text: '#166534', label: 'APPROVED' },
  in_production: { bg: '#EDE9FE', text: '#5B21B6', label: 'IN PRODUCTION' },
  ready_for_pickup: { bg: '#CCFBF1', text: '#0F766E', label: 'READY FOR PICKUP' },
  shipped: { bg: '#D1FAE5', text: '#065F46', label: 'SHIPPED' },
  delivered: { bg: '#D1FAE5', text: '#065F46', label: 'DELIVERED' },
  picked_up: { bg: '#E0F2FE', text: '#075985', label: 'PICKED UP' },
  paid: { bg: '#D1FAE5', text: '#065F46', label: 'PAID' },
  partially_paid: { bg: '#FED7AA', text: '#9A3412', label: 'PARTIAL' },
  completed: { bg: '#E0E7FF', text: '#3730A3', label: 'COMPLETED' },
  lost: { bg: '#FEE2E2', text: '#991B1B', label: 'LOST' },
  archived: { bg: '#F3F4F6', text: '#4B5563', label: 'ARCHIVED' },
};

// Statuses shown in the dropdown, in workflow order. `emailsCustomer=true`
// means changing to that status will trigger a customer email.
const STATUS_OPTIONS = [
  { key: 'draft', label: 'Draft', emailsCustomer: false, hint: 'Not sent to customer' },
  { key: 'sent', label: 'Sent', emailsCustomer: false, hint: 'Awaiting customer approval' },
  { key: 'approved', label: 'Approved', emailsCustomer: true, hint: 'Customer approved – thank-you email' },
  { key: 'in_production', label: 'In Production', emailsCustomer: true, hint: 'Notify customer production started' },
  { key: 'ready_for_pickup', label: 'Ready for Pickup', emailsCustomer: true, hint: 'Notify customer to come pick up' },
  { key: 'delivered', label: 'Delivered', emailsCustomer: true, hint: 'Notify customer of delivery' },
  { key: 'picked_up', label: 'Picked Up', emailsCustomer: true, hint: 'Customer picked up order' },
  { key: 'completed', label: 'Completed', emailsCustomer: false, hint: 'Archive as completed' },
  { key: 'lost', label: 'Lost', emailsCustomer: false, hint: 'Mark as lost opportunity' },
  { key: 'archived', label: 'Archived', emailsCustomer: false, hint: 'Hide from active views' },
];

// ---------- Small inline-edit primitive ----------
const InlineField = ({ label, value, onSave, type = 'text', placeholder = '', testId }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  useEffect(() => { setDraft(value ?? ''); }, [value]);

  const commit = async () => {
    if ((draft ?? '') === (value ?? '')) { setEditing(false); return; }
    try {
      await onSave(draft);
      setEditing(false);
    } catch (e) {
      // caller shows toast
    }
  };

  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.05em', color: '#64748B', textTransform: 'uppercase', marginBottom: 4, display: 'block' }}>
        {label}
      </label>
      {editing ? (
        <input
          autoFocus
          type={type}
          value={draft || ''}
          placeholder={placeholder}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
            if (e.key === 'Escape') { setDraft(value ?? ''); setEditing(false); }
          }}
          data-testid={testId ? `${testId}-input` : undefined}
          style={{
            width: '100%', padding: '8px 10px', border: '1px solid #3B82F6',
            borderRadius: 6, fontSize: 14, color: '#0F172A', background: '#fff',
            outline: 'none',
          }}
        />
      ) : (
        <button
          type="button"
          onClick={() => setEditing(true)}
          data-testid={testId}
          style={{
            width: '100%', textAlign: 'left', padding: '8px 10px', background: '#F8FAFC',
            border: '1px solid transparent', borderRadius: 6, fontSize: 14,
            color: value ? '#0F172A' : '#94A3B8', cursor: 'text',
            transition: 'border-color 0.15s, background 0.15s',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#E2E8F0'; e.currentTarget.style.background = '#F1F5F9'; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'transparent'; e.currentTarget.style.background = '#F8FAFC'; }}
        >
          {value || placeholder || '—'}
        </button>
      )}
    </div>
  );
};

const CollapsibleSection = ({ title, defaultOpen = false, children, testId }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ borderTop: '1px solid #E2E8F0', padding: '14px 0' }} data-testid={testId}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
          fontSize: 13, fontWeight: 600, color: '#0F172A',
        }}
      >
        <span>{title}</span>
        {open ? <ChevronDown size={16} color="#64748B" /> : <ChevronRight size={16} color="#64748B" />}
      </button>
      {open && <div style={{ marginTop: 12 }}>{children}</div>}
    </div>
  );
};

// ---------- Line item card (InkSoft-style) ----------
const ItemCard = ({ item, index, onEditNotes, onEditItem, onAttachProduct, onDesignItem, onDeleteItem }) => {
  const qty = item.quantity || Object.values(item.sizes || {}).reduce((a, b) => a + b, 0);
  const perUnitGarment = (Number(item.markup_price) || Number(item.base_price) || 0);
  // All-in per-shirt price: garment (honoring per-size overrides) + print +
  // setup fee spread across the pieces — matches what the customer sees.
  const overrides = item.size_price_overrides || {};
  const sizeRows = Object.entries(item.sizes || {}).filter(([, q]) => Number(q || 0) > 0);
  // Flat qty fallback ONLY when no size rows exist — $0 overrides (CPP) are legitimate.
  const garmentTotal = sizeRows.length > 0
    ? sizeRows.reduce((s, [sz, q]) => {
        const ov = overrides[String(sz).toUpperCase()] ?? overrides[sz];
        const p = (ov !== undefined && ov !== null && ov !== '') ? Number(ov) : perUnitGarment;
        return s + p * Number(q || 0);
      }, 0)
    : perUnitGarment * qty;
  const setupFee = Number(item.setup_fee) || 0;
  const printPerUnit = Number(item.print_cost) || 0;
  const lineTotal = garmentTotal + printPerUnit * qty + setupFee;
  const perUnit = qty > 0 ? lineTotal / qty : perUnitGarment + printPerUnit;
  const sizesStr = Object.entries(item.sizes || {})
    .filter(([, q]) => q > 0)
    .map(([s, q]) => `${s}(${q})`)
    .join('  ');
  const printMethodLabel = (item.print_method || 'Screen Printing').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  // Show the first available multi-view mockup, falling back to legacy fields.
  const mv = item.mockup_views || {};
  const firstMockup = mv.front || mv.back || mv.left || mv.right || item.mockup_url || item.mockup_base64 || null;
  const designedCount = ['front', 'back', 'left', 'right'].filter((k) => mv[k]).length
    || (item.mockup_base64 ? 1 : 0);
  const hasDesign = designedCount > 0;

  return (
    <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 8, overflow: 'hidden' }} data-testid={`item-card-${index}`}>
      <div className="pv2-itemcard-grid" style={{ padding: 20, display: 'grid', gridTemplateColumns: '92px 1fr auto', gap: 20, alignItems: 'start' }}>
        <div style={{ width: 92, height: 92, background: '#F1F5F9', borderRadius: 6, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', cursor: 'pointer' }} onClick={() => onAttachProduct(index)} title="Change product">
          {firstMockup || item.image_url ? (
            <img src={firstMockup || unwrapImg(item.image_url)} alt={item.style} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
          ) : (
            <span style={{ fontSize: 10, color: '#94A3B8', fontWeight: 600 }}>+ ADD PRODUCT</span>
          )}
          {hasDesign && (
            <span style={{ position: 'absolute', bottom: 2, right: 2, background: '#10B981', color: '#fff', fontSize: 8, fontWeight: 700, letterSpacing: '0.05em', padding: '1px 4px', borderRadius: 3 }}>
              {designedCount > 1 ? `MOCK ×${designedCount}` : 'MOCK'}
            </span>
          )}
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: '#0F172A', marginBottom: 2 }}>
            {item.is_cpp ? `Customer Provided Product - ${item.style || 'Custom'}` : `${item.brand || ''} ${item.style || ''}`.trim()}
          </div>
          <div style={{ fontSize: 12, color: '#64748B', marginBottom: 12 }}>
            {item.is_cpp ? 'Client' : (item.supplier || 'Supplier')} · {item.style || '-'}
          </div>
          <div style={{ display: 'flex', gap: 24, fontSize: 13, color: '#475569', marginBottom: 6 }}>
            <span style={{ color: '#94A3B8', fontSize: 11, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase' }}>Style:</span>
            <span style={{ color: '#0F172A', fontWeight: 500 }}>{(item.color || 'BLACK').toUpperCase()}</span>
          </div>
          <div style={{ display: 'flex', gap: 24, fontSize: 13, color: '#475569' }}>
            <span style={{ color: '#94A3B8', fontSize: 11, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase' }}>Size:</span>
            <span style={{ color: '#0F172A', fontWeight: 500 }}>
              {sizesStr || '—'}  {perUnit > 0 && (
                <span style={{ color: '#64748B', marginLeft: 8 }} data-testid={`item-allin-price-${index}`}>
                  ×  ${perUnit.toFixed(2)}/each
                  {(setupFee > 0 || printPerUnit > 0) && (
                    <span style={{ marginLeft: 6, padding: '1px 5px', background: '#F0FDF4', borderRadius: 3, fontSize: 10, fontWeight: 600, color: '#15803D', letterSpacing: '0.02em' }} title="Includes decoration + setup fee spread across pieces">
                      ALL-IN
                    </span>
                  )}
                </span>
              )}
            </span>
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 13, color: '#0F172A', fontWeight: 500 }}>
            {qty} Items / <span style={{ fontWeight: 600 }}>{fmt(lineTotal)}</span>
          </div>
          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', marginTop: 8 }}>
            <button
              onClick={() => onDesignItem(index)}
              disabled={!item.image_url}
              data-testid={`item-design-btn-${index}`}
              title={item.image_url ? 'Overlay logo on each print location' : 'Attach a product first'}
              style={{
                background: hasDesign ? '#10B981' : '#fff',
                border: `1px solid ${hasDesign ? '#10B981' : '#E2E8F0'}`,
                padding: '5px 10px', borderRadius: 6, fontSize: 12,
                color: hasDesign ? '#fff' : '#2563EB',
                fontWeight: 600, cursor: item.image_url ? 'pointer' : 'not-allowed',
                letterSpacing: '0.03em', opacity: item.image_url ? 1 : 0.5,
              }}
            >
              {hasDesign ? `✓ ${designedCount} Designed` : 'Design'}
            </button>
            <button
              onClick={() => onEditItem(index)}
              data-testid={`item-edit-btn-${index}`}
              style={{
                background: 'transparent', border: '1px solid #E2E8F0',
                padding: '5px 10px', borderRadius: 6, fontSize: 12, color: '#2563EB',
                fontWeight: 600, cursor: 'pointer', letterSpacing: '0.03em',
              }}
            >
              Edit
            </button>
            <button
              onClick={() => onDeleteItem(index)}
              data-testid={`item-delete-btn-${index}`}
              title="Delete this item from the proposal"
              style={{
                background: 'transparent', border: '1px solid #FECACA',
                padding: '5px 10px', borderRadius: 6, fontSize: 12, color: '#DC2626',
                fontWeight: 600, cursor: 'pointer', letterSpacing: '0.03em',
              }}
            >
              Delete
            </button>
          </div>
        </div>
      </div>

      {(item.decoration_notes || item.print_method) && (
        <div style={{ background: '#F8FAFC', borderTop: '1px solid #E2E8F0', padding: '16px 20px' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#0F172A', marginBottom: 8 }}>
            {printMethodLabel}
          </div>
          {item.decoration_notes && (
            <div style={{ fontSize: 13, color: '#475569', whiteSpace: 'pre-line', lineHeight: 1.55 }}>
              {item.decoration_notes}
            </div>
          )}
          <button
            onClick={() => onEditNotes(index)}
            data-testid={`item-edit-notes-${index}`}
            style={{
              marginTop: 10, background: 'transparent', border: 'none', padding: 0,
              color: '#2563EB', fontSize: 13, cursor: 'pointer', fontWeight: 500,
            }}
          >
            Edit notes
          </button>
        </div>
      )}
    </div>
  );
};

// ---------- Group section header (Estimated Art / Front · 1 Color · Setup Fee) ----------
const GroupHeader = ({ groupLabel, meta, setupFee }) => (
  <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 8, padding: '18px 22px', marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
    <div>
      <div style={{ fontSize: 15, fontWeight: 600, color: '#0F172A', marginBottom: 6 }}>{groupLabel}</div>
      {meta && <div style={{ fontSize: 13, color: '#64748B', whiteSpace: 'pre-line', lineHeight: 1.55 }}>{meta}</div>}
    </div>
    {setupFee > 0 && (
      <div style={{ fontSize: 13, color: '#0F172A' }}>Setup Fee: <span style={{ fontWeight: 600 }}>{fmt(setupFee)}</span></div>
    )}
  </div>
);

// ---------- Main page ----------
const ProposalViewV2 = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [proposal, setProposal] = useState(null);
  const [garmentMarkup, setGarmentMarkup] = useState(2.1);
  useEffect(() => { axios.get('/pricing/policy').then((r) => setGarmentMarkup(r.data.garment_markup || 2.1)).catch(() => {}); }, []);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAdminPayment, setShowAdminPayment] = useState(false);
  const [showEmailPanel, setShowEmailPanel] = useState(false);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [discountDraft, setDiscountDraft] = useState(0);
  const [notesDraftIdx, setNotesDraftIdx] = useState(null);
  const [notesDraft, setNotesDraft] = useState('');
  const [editItemIdx, setEditItemIdx] = useState(null);
  const [showFeesModal, setShowFeesModal] = useState(false);
  const [feesDraft, setFeesDraft] = useState({ setup_fees: 0, additional_fees: 0 });
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerItemIdx, setPickerItemIdx] = useState(null); // null = add-new, number = update existing
  const [designerItemIdx, setDesignerItemIdx] = useState(null);
  const [designerSaving, setDesignerSaving] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);
  const [statusConfirm, setStatusConfirm] = useState(null); // {status, emailsCustomer, label}
  const [statusSaving, setStatusSaving] = useState(false);
  const [pickupOpen, setPickupOpen] = useState(false);
  const [pickupSaving, setPickupSaving] = useState(false);

  const fetchProposal = async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      const res = await axios.get(`/proposals/${id}`);
      setProposal(res.data);
    } catch (e) {
      toast.error('Failed to load proposal');
      navigate('/');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => { fetchProposal(); }, [id]);

  const patchHeader = async (fields) => {
    setSaving(true);
    try {
      const res = await axios.patch(`/proposals/${id}/header`, fields);
      setProposal(res.data);
      toast.success('Saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
      throw e;
    } finally {
      setSaving(false);
    }
  };

  const saveDiscount = async () => {
    await patchHeader({ discount_amount: Number(discountDraft) || 0 });
    setShowDiscountModal(false);
  };

  const handleProductPicked = async (product) => {
    // Called by ProductPickerModal → attach or replace product on an item.
    setSaving(true);
    try {
      const items = [...(proposal.items || [])];
      // Normalize per-size wholesale prices coming from SanMar CSV so the
      // pricing engine + EditProductModal don't have to guess 2XL/3XL/etc.
      const sizeCosts = {};
      Object.entries(product.sizePrices || {}).forEach(([k, v]) => {
        if (v != null && v !== '') sizeCosts[String(k).toUpperCase()] = Number(v);
      });
      const patch = {
        brand: product.brandName,
        style: product.styleID,
        style_name: product.styleName,
        color: product.colorName,
        image_url: product.imageUrl,
        // Per-angle photos (front / back / left / right) so the Mockup Designer
        // can render each print location on the correct garment photograph.
        views: product.views || null,
        supplier: product.supplier,
        base_price: Number(product.basePrice) || 0,
        // Per-size wholesale used by EditProductModal + backend pricing engine.
        // Persisted so the modal shows real 2XL / 3XL / 4XL upcharges instead
        // of the legacy hardcoded matrix.
        size_costs: sizeCosts,
        garment_cost: Number(product.basePrice) || 0,
        // Preserve markup_price if already set (even literal 0); otherwise
        // seed from base × 1.7 as a starting quote.
        markup_price:
          (pickerItemIdx != null && items[pickerItemIdx]?.markup_price != null)
            ? items[pickerItemIdx].markup_price
            : Math.round(((product.basePrice || 0) * garmentMarkup) * 100) / 100,
        // Per-size sell = actual wholesale × company markup (no flat surcharge on top)
        size_price_overrides: Object.fromEntries(Object.entries(sizeCosts).map(([sz, c]) => [sz, Math.round(c * garmentMarkup * 100) / 100])),
        garment_markup: garmentMarkup,
      };
      const isCpp = pickerItemIdx != null && !!items[pickerItemIdx]?.is_cpp;
      if (isCpp) {
        // Customer Provided Product: the pick is ONLY for mockup photos —
        // the customer supplies the garment, so its price must stay $0.
        patch.base_price = 0;
        patch.garment_cost = 0;
        patch.size_costs = {};
        patch.markup_price = items[pickerItemIdx]?.markup_price ?? 0;
      }
      if (pickerItemIdx == null) {
        // Add new item — default 1 qty in the first available size.
        const firstSize = (product.sizes && product.sizes[0]) || 'M';
        items.push({
          ...patch,
          sizes: { [firstSize]: 1 },
          quantity: 1,
          print_method: 'screen_print',
          setup_fee: 0,
        });
      } else {
        // Replacing product → clear the old mockup so stale composites don't
        // stick around on a different garment/color.
        items[pickerItemIdx] = {
          ...items[pickerItemIdx],
          ...patch,
          mockup_base64: null,
          mockup_url: null,
          mockup_designs: null,
          mockup_views: null,
        };
      }
      const res = await axios.patch(`/proposals/${id}/items`, { items });
      setProposal(res.data.proposal || res.data);
      toast.success(pickerItemIdx == null ? 'Product added' : 'Product attached');
      setPickerOpen(false);
      setPickerItemIdx(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to attach product');
    } finally {
      setSaving(false);
    }
  };

  const handleDesignSaved = async ({ mockup_views, mockup_designs, mockup_url, views_override }) => {
    if (designerItemIdx == null) return;
    setDesignerSaving(true);
    try {
      // Persist the heavy per-view composites into the mockup_designs collection
      // (kept out of the proposal doc to stay under Mongo's 16MB limit).
      const saveRes = await axios.put(
        `/proposals/${id}/items/${designerItemIdx}/mockup-design`,
        {
          sides: mockup_designs || {},           // editable state per view
          mockup_views: mockup_views || {},       // rendered composites per view
          mockup_url: mockup_url || null,         // small thumbnail
          views_override: views_override || {},   // custom garment photos per view
        }
      );
      // Reflect the new mockup state on the local item so the ItemCard
      // immediately shows the composite without a full refetch.
      const items = [...(proposal.items || [])];
      items[designerItemIdx] = {
        ...items[designerItemIdx],
        mockup_designs: mockup_designs || null,
        mockup_views: mockup_views || null,
        mockup_url: mockup_url || items[designerItemIdx]?.mockup_url,
        mockup_design_id: saveRes.data?.mockup_design_id || items[designerItemIdx]?.mockup_design_id,
        views_override: views_override || null,
        // Legacy single-mockup mirror so any un-migrated screens still show something.
        mockup_base64: mockup_url || null,
      };
      setProposal({ ...proposal, items });
      const count = Object.values(mockup_views || {}).filter(Boolean).length;
      toast.success(count > 1 ? `Saved ${count} mockups` : 'Mockup saved');
      setDesignerItemIdx(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save mockup');
    } finally {
      setDesignerSaving(false);
    }
  };

  const saveFees = async () => {
    const setup = parseFloat(feesDraft.setup_fees);
    const addl = parseFloat(feesDraft.additional_fees);
    if (isNaN(setup) || setup < 0 || isNaN(addl) || addl < 0) {
      toast.error('Fees must be non-negative numbers');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.patch(`/proposals/${id}/breakdown`, {
        setup_fees: setup,
        additional_fees: addl,
      });
      // /breakdown returns { success, proposal, preview }
      setProposal(res.data.proposal || res.data);
      toast.success('Fees updated');
      setShowFeesModal(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update fees');
    } finally {
      setSaving(false);
    }
  };

  const deleteItem = async (idx) => {
    const it = proposal?.items?.[idx];
    const label = `${it?.brand || ''} ${it?.style || ''}`.trim() || `Item #${idx + 1}`;
    if (!window.confirm(`Delete "${label}" from this proposal? Totals will be recalculated. This can't be undone.`)) return;
    try {
      const res = await axios.delete(`/proposals/${id}/items/${idx}`);
      setProposal(res.data.proposal || res.data);
      toast.success('Item deleted — totals updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete item');
    }
  };

  const saveItemNotes = async () => {
    if (notesDraftIdx == null) return;
    setSaving(true);
    try {
      const newItems = [...(proposal.items || [])];
      newItems[notesDraftIdx] = { ...newItems[notesDraftIdx], decoration_notes: notesDraft };
      const res = await axios.patch(`/proposals/${id}/items`, { items: newItems });
      // The endpoint might return the updated proposal or wrap it
      setProposal(res.data.proposal || res.data);
      toast.success('Notes updated');
      setNotesDraftIdx(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save notes');
    } finally {
      setSaving(false);
    }
  };

  const handleSendEmail = async () => {
    setSendingEmail(true);
    try {
      await axios.post(`/proposals/${id}/send-email`);
      toast.success(`Proposal emailed to ${proposal.customer_email}`);
      await fetchProposal();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleDownloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`/proposals/${id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `${proposal.proposal_number}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('PDF download failed');
    } finally {
      setDownloading(false);
    }
  };

  const copyCustomerLink = async () => {
    try {
      const { data } = await axios.get(`/proposals/${id}/share-link`);
      await navigator.clipboard.writeText(data.url);
      toast.success('Customer link copied');
    } catch { toast.error('Could not create the customer link'); }
  };

  const applyStatusChange = async (statusKey, sendCustomerEmail) => {
    setStatusSaving(true);
    try {
      const res = await axios.post(`/proposals/${id}/status`, {
        status: statusKey,
        send_customer_email: sendCustomerEmail,
      });
      setProposal(res.data);
      toast.success(
        [res.data._order_created ? `Order ${res.data._order_po_number} created` : 'Status updated',
         res.data._email_sent ? `Email sent to ${proposal.customer_email}` : null].filter(Boolean).join(' · ')
      );
      setStatusConfirm(null);
      setStatusMenuOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Status change failed');
    } finally {
      setStatusSaving(false);
    }
  };

  const onStatusPick = (opt) => {
    if (opt.key === proposal.status) { setStatusMenuOpen(false); return; }
    // Picked Up REQUIRES a signature — open the sign-off capture instead.
    if (opt.key === 'picked_up' && !proposal.pickup_record) {
      setStatusMenuOpen(false);
      setPickupOpen(true);
      return;
    }
    // If the change triggers a customer email, show confirmation first.
    // Otherwise, apply immediately.
    if (opt.emailsCustomer && proposal.customer_email) {
      setStatusConfirm(opt);
      setStatusMenuOpen(false);
    } else {
      applyStatusChange(opt.key, false);
    }
  };

  const handlePickupSubmit = async (payload) => {
    setPickupSaving(true);
    try {
      const res = await axios.post(`/proposals/${id}/pickup`, payload);
      setProposal(res.data);
      toast.success(
        res.data._email_sent
          ? `Pickup recorded · Confirmation emailed to ${res.data.customer_email}`
          : 'Pickup recorded'
      );
      setPickupOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not record pickup');
    } finally {
      setPickupSaving(false);
    }
  };

  const totals = useMemo(() => {
    if (!proposal) return null;
    const shirt = Number(proposal.shirt_total) || 0;
    const printT = Number(proposal.print_total) || 0;
    const setupF = Number(proposal.setup_fees) || 0;
    const addl = Number(proposal.additional_fees) || 0;
    const disc = Number(proposal.discount_amount) || 0;
    const sub = Number(proposal.subtotal) || (shirt + printT + setupF + addl - disc);
    const tax = Number(proposal.tax_amount) || 0;
    const total = Number(proposal.total_amount) || sub + tax;
    const paid = (proposal.payments || []).reduce((s, p) => s + Number(p.amount || 0), 0);
    const balance = Math.max(total - paid, 0);
    const itemCount = (proposal.items || []).reduce((s, it) => s + (it.quantity || Object.values(it.sizes || {}).reduce((a, b) => a + b, 0)), 0);
    return { shirt, printT, setupF, addl, disc, sub, tax, total, paid, balance, itemCount };
  }, [proposal]);

  // Group items by print_method
  const groupedItems = useMemo(() => {
    if (!proposal?.items) return [];
    const groups = {};
    proposal.items.forEach((it, idx) => {
      const key = it.design_group || it.print_method || 'items';
      if (!groups[key]) groups[key] = { key, items: [], locations: new Set(), colors: 0, method: it.print_method };
      groups[key].items.push({ ...it, __idx: idx });
      (it.print_locations || []).forEach((l) => groups[key].locations.add(l));
      const c = Object.values(it.print_colors || {}).reduce((a, b) => Math.max(a, b), 0);
      groups[key].colors = Math.max(groups[key].colors, c);
    });
    return Object.values(groups);
  }, [proposal]);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F8FAFC' }}>
        <Loader2 size={32} className="animate-spin" color="#64748B" />
      </div>
    );
  }
  if (!proposal || !totals) return null;

  const status = STATUS_STYLES[proposal.status] || STATUS_STYLES.draft;
  const initials = (proposal.customer_name || '?').split(' ').map((s) => s[0]).slice(0, 2).join('').toUpperCase();

  return (
    <div style={{ minHeight: '100vh', background: '#F8FAFC', color: '#0F172A' }} data-testid="proposal-view-v2">
      {/* ===== TOP BAR ===== */}
      <div className="pv2-topbar" style={{ background: '#fff', borderBottom: '1px solid #E2E8F0', padding: '14px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <button onClick={() => navigate('/')} data-testid="back-btn" style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 6, borderRadius: 4, display: 'flex' }} title="Back to dashboard">
            <ArrowLeft size={20} color="#475569" />
          </button>
          <div style={{ fontSize: 16, fontWeight: 600, color: '#0F172A' }} data-testid="proposal-number">
            {proposal.proposal_number}
          </div>
        </div>
        <div className="pv2-topbar-actions" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button
            onClick={() => navigate(`/proposal/${id}/legacy`)}
            data-testid="open-legacy-btn"
            title="Open the classic view with mockup editor + production pack"
            style={{ background: '#fff', border: '1px solid #E2E8F0', padding: '8px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 12, color: '#475569', fontWeight: 600, letterSpacing: '0.03em' }}
          >
            Advanced View
          </button>
          <button
            onClick={handleDownloadPdf}
            disabled={downloading}
            data-testid="download-pdf-btn"
            style={{ background: '#fff', border: '1px solid #E2E8F0', padding: '8px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 13, color: '#0F172A', display: 'flex', alignItems: 'center', gap: 6, fontWeight: 500 }}
          >
            {downloading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} PDF
          </button>
          <button
            onClick={handleSendEmail}
            disabled={sendingEmail || !proposal.customer_email}
            data-testid="send-email-btn"
            style={{ background: '#0F172A', color: '#fff', border: 'none', padding: '9px 16px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}
          >
            {sendingEmail ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send to Customer
          </button>
        </div>
      </div>

      {/* ===== SUMMARY BAR ===== */}
      <div className="pv2-summarybar" style={{ background: '#fff', borderBottom: '1px solid #E2E8F0', padding: '18px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div className="pv2-summary-info" style={{ display: 'flex', gap: 40 }}>
          <div>
            <div style={{ fontSize: 11, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#94A3B8', fontWeight: 600 }}>In Hands Date</div>
            <div style={{ fontSize: 14, color: '#0F172A', marginTop: 3 }}>{proposal.in_hands_date || '—'}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#94A3B8', fontWeight: 600 }}>Expiration Date</div>
            <div style={{ fontSize: 14, color: '#0F172A', marginTop: 3 }}>
              {proposal.expiry_date ? new Date(proposal.expiry_date).toLocaleDateString() : '—'}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 11, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#94A3B8', fontWeight: 600 }}>Total</div>
            <div style={{ fontSize: 20, color: '#0F172A', marginTop: 1, fontWeight: 700 }} data-testid="header-total">{fmt(totals.total)}</div>
          </div>
        </div>
        <div className="pv2-summary-actions" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button onClick={() => setShowEmailPanel(!showEmailPanel)} data-testid="toggle-email-panel-btn" title="Email activity" style={{ background: '#fff', border: '1px solid #E2E8F0', padding: 8, borderRadius: 6, cursor: 'pointer', display: 'flex' }}>
            <MessageSquare size={16} color="#475569" />
          </button>
          <button onClick={copyCustomerLink} data-testid="copy-link-btn" title="Copy customer link" style={{ background: '#fff', border: '1px solid #E2E8F0', padding: 8, borderRadius: 6, cursor: 'pointer', display: 'flex' }}>
            <ExternalLink size={16} color="#475569" />
          </button>
          {!proposal.pickup_record && (
            <button onClick={() => setPickupOpen(true)} data-testid="record-pickup-btn" title="Record pickup with signature" style={{ background: '#0369A1', color: '#fff', border: 'none', padding: '8px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
              <PenLine size={14} /> Record Pickup
            </button>
          )}
          <button onClick={() => setShowAdminPayment(true)} data-testid="take-payment-btn" title="Take payment" style={{ background: '#10B981', color: '#fff', border: 'none', padding: '8px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
            <CreditCard size={14} /> Take Payment
          </button>
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setStatusMenuOpen((v) => !v)}
              data-testid="status-pill"
              style={{
                background: status.bg, color: status.text, padding: '6px 10px 6px 12px',
                borderRadius: 6, fontSize: 12, fontWeight: 700, letterSpacing: '0.05em',
                border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              {status.label}
              <ChevronDown size={13} />
            </button>
            {statusMenuOpen && (
              <>
                <div
                  onClick={() => setStatusMenuOpen(false)}
                  style={{ position: 'fixed', inset: 0, zIndex: 40 }}
                />
                <div
                  data-testid="status-menu"
                  style={{
                    position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 50,
                    background: '#fff', border: '1px solid #E2E8F0', borderRadius: 8,
                    boxShadow: '0 12px 32px rgba(15,23,42,0.12)', minWidth: 280, padding: 6,
                  }}
                >
                  {STATUS_OPTIONS.map((opt) => {
                    const active = opt.key === proposal.status;
                    const optStyle = STATUS_STYLES[opt.key] || STATUS_STYLES.draft;
                    return (
                      <button
                        key={opt.key}
                        onClick={() => onStatusPick(opt)}
                        data-testid={`status-option-${opt.key}`}
                        style={{
                          width: '100%', textAlign: 'left', background: active ? '#F8FAFC' : '#fff',
                          border: 'none', padding: '10px 12px', cursor: 'pointer', borderRadius: 6,
                          display: 'flex', alignItems: 'center', gap: 10,
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = '#F1F5F9')}
                        onMouseLeave={(e) => (e.currentTarget.style.background = active ? '#F8FAFC' : '#fff')}
                      >
                        <span style={{
                          display: 'inline-block', width: 8, height: 8, borderRadius: 999,
                          background: optStyle.text, flexShrink: 0,
                        }} />
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#0F172A' }}>{opt.label}</div>
                          <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 1 }}>
                            {opt.hint}{opt.emailsCustomer ? ' ✉︎' : ''}
                          </div>
                        </span>
                        {active && <Check size={14} color="#10B981" />}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ===== GRID ===== */}
      <div className="pv2-layout" style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: 0, alignItems: 'start' }}>
        {/* --- LEFT SIDEBAR --- */}
        <aside className="pv2-sidebar" style={{ background: '#fff', borderRight: '1px solid #E2E8F0', minHeight: 'calc(100vh - 133px)', padding: 24 }}>
          {/* Customer */}
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 14 }}>
            <div style={{ width: 44, height: 44, borderRadius: 999, background: '#EF4444', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 15 }} data-testid="customer-avatar">
              {initials}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: '#0F172A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} data-testid="customer-name-display">
                {proposal.customer_name || 'Customer'}
              </div>
            </div>
          </div>
          <div style={{ fontSize: 12, color: '#64748B', marginBottom: 4, display: 'flex', gap: 6, alignItems: 'center' }}>
            <Mail size={12} color="#94A3B8" /> {proposal.customer_email || '—'}
          </div>
          <div style={{ fontSize: 12, color: '#64748B', marginBottom: 20, display: 'flex', gap: 6, alignItems: 'center' }}>
            <Phone size={12} color="#94A3B8" /> {proposal.customer_phone || '—'}
          </div>

          {/* Proposal Info */}
          <CollapsibleSection title="Proposal Info" defaultOpen testId="section-proposal-info">
            <div style={{ fontSize: 11, color: '#94A3B8', marginBottom: 10 }}>
              Created {proposal.created_at ? new Date(proposal.created_at).toLocaleDateString() : '—'}
            </div>
            <InlineField label="Customer Name" value={proposal.customer_name} testId="edit-customer-name" onSave={(v) => patchHeader({ customer_name: v })} />
            <InlineField label="Customer Email" value={proposal.customer_email} type="email" testId="edit-customer-email" onSave={(v) => patchHeader({ customer_email: v })} />
            <InlineField label="Customer Phone" value={proposal.customer_phone} type="tel" testId="edit-customer-phone" onSave={(v) => patchHeader({ customer_phone: v })} />
            <InlineField label="PO Number" value={proposal.po_number} placeholder="Optional" testId="edit-po-number" onSave={(v) => patchHeader({ po_number: v })} />
            <InlineField
              label="In Hands Date"
              value={proposal.in_hands_date}
              type="date"
              testId="edit-in-hands-date"
              onSave={(v) => patchHeader({ in_hands_date: v })}
            />
            <InlineField
              label="Expiration Date"
              value={proposal.expiry_date ? proposal.expiry_date.slice(0, 10) : ''}
              type="date"
              testId="edit-expiry-date"
              onSave={(v) => patchHeader({ expiry_date: v ? new Date(v).toISOString() : null })}
            />
            <InlineField label="Notes" value={proposal.notes} placeholder="Internal notes" testId="edit-notes" onSave={(v) => patchHeader({ notes: v })} />
          </CollapsibleSection>

          {/* Payment Details */}
          <CollapsibleSection title="Payment Details" testId="section-payment">
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6 }}>
              <span style={{ color: '#64748B' }}>Total</span>
              <span style={{ color: '#0F172A', fontWeight: 500 }}>{fmt(totals.total)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6 }}>
              <span style={{ color: '#64748B' }}>Paid</span>
              <span style={{ color: '#10B981', fontWeight: 500 }} data-testid="sidebar-paid">{fmt(totals.paid)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 12 }}>
              <span style={{ color: '#64748B' }}>Balance</span>
              <span style={{ color: totals.balance > 0 ? '#EA580C' : '#0F172A', fontWeight: 600 }} data-testid="sidebar-balance">{fmt(totals.balance)}</span>
            </div>
            {/* Payment permissions — admin-only partial payments (default OFF) + invoicing / payment terms. */}
            <PaymentPermissionsPanel proposal={proposal} isAdmin={user?.role === 'admin'} onUpdated={() => fetchProposal({ silent: true })} />
            {totals.balance > 0 && (
              <button
                onClick={() => setShowAdminPayment(true)}
                data-testid="sidebar-take-payment-btn"
                style={{ width: '100%', background: '#10B981', color: '#fff', border: 'none', padding: '9px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
              >
                <CreditCard size={14} /> Take Payment
              </button>
            )}
            <PaymentTransactions proposal={proposal} onRefunded={fetchProposal} />
          </CollapsibleSection>

          {/* Pricing / Fees */}
          <CollapsibleSection title="Pricing & Fees" testId="section-fees">
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6 }}>
              <span style={{ color: '#64748B' }}>Setup Fees</span>
              <span style={{ color: '#0F172A', fontWeight: 500 }} data-testid="sidebar-setup-fees">{fmt(totals.setupF)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6 }}>
              <span style={{ color: '#64748B' }}>Additional Fees</span>
              <span style={{ color: '#0F172A', fontWeight: 500 }} data-testid="sidebar-additional-fees">{fmt(totals.addl)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 12 }}>
              <span style={{ color: '#64748B' }}>Discount</span>
              <span style={{ color: totals.disc > 0 ? '#EA580C' : '#0F172A', fontWeight: 500 }}>{totals.disc > 0 ? `−${fmt(totals.disc)}` : fmt(0)}</span>
            </div>
            <button
              onClick={() => {
                setFeesDraft({
                  setup_fees: Number(proposal.setup_fees) || 0,
                  additional_fees: Number(proposal.additional_fees) || 0,
                });
                setShowFeesModal(true);
              }}
              data-testid="sidebar-edit-fees-btn"
              style={{ width: '100%', background: '#fff', border: '1px solid #E2E8F0', color: '#0F172A', padding: '8px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600, letterSpacing: '0.03em', textTransform: 'uppercase' }}
            >
              Edit Setup & Additional Fees
            </button>
          </CollapsibleSection>

          {/* Shipping Details — Shippo label purchase + tracking */}
          <CollapsibleSection title="Shipping Details" testId="section-shipping" defaultOpen={!!proposal.shipping}>
            <ShippingPanel proposal={proposal} onUpdated={fetchProposal} />
          </CollapsibleSection>

          {/* Delivery Details */}
          <CollapsibleSection title="Delivery Details" testId="section-delivery">
            <div style={{ fontSize: 13, color: '#64748B', marginBottom: 6 }}>
              Pickup at {brandName()} <span style={{ marginLeft: 6, padding: '2px 6px', background: '#F1F5F9', borderRadius: 4, fontSize: 10, fontWeight: 600, color: '#64748B', letterSpacing: '0.03em' }}>NON-TAXABLE</span>
            </div>
            <div style={{ fontSize: 12, color: '#94A3B8' }}>Free · No shipping charge</div>
          </CollapsibleSection>

          {/* Pickup Proof */}
          {proposal.pickup_record && (
            <CollapsibleSection title="Pickup Proof" defaultOpen testId="section-pickup-proof">
              <div style={{ fontSize: 13, marginBottom: 4 }}>
                <span style={{ color: '#64748B' }}>Picked up by </span>
                <span style={{ color: '#0F172A', fontWeight: 600 }} data-testid="pickup-proof-name">{proposal.pickup_record.picked_up_by}</span>
              </div>
              <div style={{ fontSize: 12, color: '#94A3B8', marginBottom: 10 }} data-testid="pickup-proof-time">
                {new Date(proposal.pickup_record.picked_up_at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
                {proposal.pickup_record.recorded_by ? ` · by ${proposal.pickup_record.recorded_by}` : ''}
              </div>
              {proposal.pickup_record.photo_base64 && (
                <img src={proposal.pickup_record.photo_base64} alt="Person who picked up" data-testid="pickup-proof-photo" style={{ width: 96, height: 96, objectFit: 'cover', borderRadius: 8, border: '1px solid #E2E8F0', marginBottom: 10, display: 'block' }} />
              )}
              <div style={{ fontSize: 10, fontWeight: 700, color: '#94A3B8', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 4 }}>Signature</div>
              <img src={proposal.pickup_record.signature_base64} alt="Pickup signature" data-testid="pickup-proof-signature" style={{ width: '100%', background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 8 }} />
            </CollapsibleSection>
          )}

          {/* Advanced */}
          <CollapsibleSection title="Advanced" testId="section-advanced">
            <button onClick={() => navigate(`/proposal/${id}/edit`)} data-testid="open-full-edit-btn" style={{ width: '100%', textAlign: 'left', background: 'transparent', border: 'none', padding: '8px 0', cursor: 'pointer', fontSize: 13, color: '#2563EB', fontWeight: 500 }}>
              Open Full Editor →
            </button>
            <button onClick={() => navigate(`/proposal/${id}/mockup-designer`)} data-testid="open-mockup-designer-btn" style={{ width: '100%', textAlign: 'left', background: 'transparent', border: 'none', padding: '8px 0', cursor: 'pointer', fontSize: 13, color: '#2563EB', fontWeight: 500 }}>
              Mockup Designer →
            </button>
            <button onClick={() => navigate(`/ai-assistant?edit=${proposal.proposal_number}`)} data-testid="open-ai-edit-btn" style={{ width: '100%', textAlign: 'left', background: 'transparent', border: 'none', padding: '8px 0', cursor: 'pointer', fontSize: 13, color: '#2563EB', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Sparkles size={13} /> Edit with AI →
            </button>
          </CollapsibleSection>
        </aside>

        {/* --- MAIN CONTENT --- */}
        <main className="pv2-main" style={{ padding: 28, display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 340px', gap: 24, alignItems: 'start' }}>
          {/* Items column */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 18 }}>
              <div style={{ fontSize: 22, fontWeight: 600, color: '#0F172A' }} data-testid="section-title">
                {proposal.customer_name || 'Order'}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ fontSize: 13, color: '#64748B' }} data-testid="item-count-badge">
                  {totals.itemCount} Items
                </div>
                <button
                  onClick={() => { setPickerItemIdx(null); setPickerOpen(true); }}
                  data-testid="add-item-btn"
                  style={{ background: '#0F172A', color: '#fff', border: 'none', padding: '8px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
                >
                  + Add Item
                </button>
              </div>
            </div>

            {groupedItems.length === 0 && (
              <button
                onClick={() => { setPickerItemIdx(null); setPickerOpen(true); }}
                data-testid="empty-add-item-btn"
                style={{ width: '100%', padding: 40, textAlign: 'center', background: '#fff', border: '1px dashed #CBD5E1', borderRadius: 8, color: '#475569', cursor: 'pointer', fontSize: 14, fontWeight: 500 }}
              >
                No items yet · <span style={{ color: '#2563EB', fontWeight: 600 }}>+ Add Product from SanMar Catalog</span>
              </button>
            )}

            {groupedItems.map((g, gi) => {
              const meta = [
                Array.from(g.locations).map((l) => l.replace(/_/g, ' ')).map((l) => l.charAt(0).toUpperCase() + l.slice(1)).join(' · ') || null,
                g.colors > 0 ? `${g.colors} Color${g.colors === 1 ? '' : 's'}` : null,
              ].filter(Boolean).join('\n');
              const setupPer = g.items.reduce((s, it) => s + (Number(it.setup_fee) || 0), 0);
              return (
                <div key={gi} style={{ marginBottom: 20 }}>
                  <GroupHeader
                    groupLabel="Estimated Art"
                    meta={meta}
                    setupFee={setupPer || (gi === 0 ? totals.setupF : 0)}
                  />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {g.items.map((it) => (
                      <ItemCard
                        key={it.__idx}
                        item={it}
                        index={it.__idx}
                        onEditNotes={(idx) => {
                          setNotesDraftIdx(idx);
                          setNotesDraft(proposal.items[idx]?.decoration_notes || '');
                        }}
                        onEditItem={(idx) => setEditItemIdx(idx)}
                        onDeleteItem={deleteItem}
                        onAttachProduct={(idx) => { setPickerItemIdx(idx); setPickerOpen(true); }}
                        onDesignItem={async (idx) => {
                          // Fetch the heavy mockup-design state (stored in the
                          // mockup_designs collection, not the proposal doc)
                          // and hydrate the item before opening the designer,
                          // so previously-saved logos/positions/photos per view
                          // survive reloads.
                          try {
                            const res = await axios.get(`/proposals/${id}/items/${idx}/mockup-design`);
                            const doc = res.data || {};
                            if (doc.sides || doc.mockup_views || doc.views_override) {
                              const items = [...(proposal.items || [])];
                              items[idx] = {
                                ...items[idx],
                                mockup_designs: doc.sides || items[idx]?.mockup_designs || null,
                                mockup_views: doc.mockup_views || items[idx]?.mockup_views || null,
                                views_override: doc.views_override || items[idx]?.views_override || null,
                              };
                              setProposal({ ...proposal, items });
                            }
                          } catch (e) {
                            // Non-fatal: still open the designer with a blank slate.
                          }
                          setDesignerItemIdx(idx);
                        }}
                      />
                    ))}
                  </div>
                </div>
              );
            })}

            {/* Email Activity (drawer) */}
            {showEmailPanel && (
              <div style={{ marginTop: 24, background: '#fff', border: '1px solid #E2E8F0', borderRadius: 8, padding: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <div style={{ fontSize: 15, fontWeight: 600, color: '#0F172A' }}>Email Activity</div>
                  <button onClick={() => setShowEmailPanel(false)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4 }}>
                    <X size={16} color="#64748B" />
                  </button>
                </div>
                <EmailActivityPanel proposalId={id} />
              </div>
            )}
          </div>

          {/* Order summary panel */}
          <aside style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 8, padding: 22, position: 'sticky', top: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 10 }}>
              <span style={{ color: '#64748B' }}>Order Item Count</span>
              <span style={{ color: '#0F172A', fontWeight: 500 }}>{totals.itemCount}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 10 }}>
              <span style={{ color: '#64748B' }}>Item Subtotal</span>
              <span style={{ color: '#0F172A', fontWeight: 500 }}>{fmt(totals.shirt)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 10 }}>
              <span style={{ color: '#64748B' }}>Total Print Price</span>
              <span style={{ color: '#0F172A', fontWeight: 500 }}>{fmt(totals.printT + totals.setupF)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 10 }}>
              <span style={{ color: '#64748B' }}>Total Services</span>
              <span style={{ color: '#0F172A', fontWeight: 500 }}>{fmt(totals.addl)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 14, alignItems: 'center' }}>
              <span style={{ color: '#64748B' }}>Pickup at {brandName()} <span style={{ marginLeft: 4, padding: '1px 5px', background: '#F1F5F9', borderRadius: 3, fontSize: 10, fontWeight: 600, color: '#64748B' }}>NON-TAXABLE</span></span>
              <span style={{ color: '#0F172A', fontWeight: 500 }}>FREE</span>
            </div>
            <div style={{ borderTop: '1px solid #E2E8F0', paddingTop: 14 }}>
              {totals.disc > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 10 }}>
                  <span style={{ color: '#EA580C' }}>Discount</span>
                  <span style={{ color: '#EA580C', fontWeight: 500 }} data-testid="summary-discount">−{fmt(totals.disc)}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 10 }}>
                <span style={{ color: '#64748B' }}>Subtotal</span>
                <span style={{ color: '#0F172A', fontWeight: 500 }} data-testid="summary-subtotal">{fmt(totals.sub)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 16 }}>
                <span style={{ color: '#64748B' }}>Tax</span>
                <span style={{ color: '#0F172A', fontWeight: 500 }} data-testid="summary-tax">{fmt(totals.tax)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', paddingTop: 14, borderTop: '1px solid #E2E8F0' }}>
                <span style={{ fontSize: 16, fontWeight: 600, color: '#0F172A' }}>Total</span>
                <div>
                  <span style={{ fontSize: 11, color: '#94A3B8', marginRight: 4 }}>USD</span>
                  <span style={{ fontSize: 22, fontWeight: 700, color: '#0F172A' }} data-testid="summary-total">{fmt(totals.total)}</span>
                </div>
              </div>
              <InternalPricingCard internal={proposal.internal_pricing} />
              <button
                onClick={() => {
                  setDiscountDraft(totals.disc || 0);
                  setShowDiscountModal(true);
                }}
                data-testid="add-discount-btn"
                style={{ width: '100%', marginTop: 16, background: '#fff', border: '1px solid #E2E8F0', padding: '11px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 13, color: '#0F172A', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
              >
                <DollarSign size={14} /> {totals.disc > 0 ? 'Edit Discount' : 'Add Discount'}
              </button>
            </div>
          </aside>
        </main>
      </div>

      {/* ===== ADMIN PAYMENT MODAL ===== */}
      {showAdminPayment && (
        <AdminPaymentModal
          proposal={proposal}
          onClose={() => setShowAdminPayment(false)}
          onPaymentRecorded={fetchProposal}
        />
      )}

      {/* ===== DISCOUNT MODAL ===== */}
      {showDiscountModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60 }} onClick={() => setShowDiscountModal(false)} data-testid="discount-modal">
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', width: 400, borderRadius: 10, overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ padding: '18px 22px', borderBottom: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: 16, fontWeight: 600 }}>Add Discount</div>
              <button onClick={() => setShowDiscountModal(false)} style={{ background: 'transparent', border: 'none', padding: 4, cursor: 'pointer' }}>
                <X size={18} color="#64748B" />
              </button>
            </div>
            <div style={{ padding: 22 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#64748B', marginBottom: 8 }}>Discount Amount ($)</label>
              <input
                type="number" min="0" step="0.01"
                value={discountDraft}
                onChange={(e) => setDiscountDraft(e.target.value)}
                autoFocus
                data-testid="discount-input"
                style={{ width: '100%', padding: '10px 12px', border: '1px solid #CBD5E1', borderRadius: 6, fontSize: 15, color: '#0F172A' }}
              />
              <div style={{ fontSize: 12, color: '#94A3B8', marginTop: 8 }}>
                Subtracts from subtotal. Tax + total recalculate automatically.
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
                <button onClick={() => setShowDiscountModal(false)} style={{ flex: 1, padding: '10px 14px', background: '#fff', border: '1px solid #E2E8F0', borderRadius: 6, cursor: 'pointer', fontSize: 13, color: '#475569', fontWeight: 500 }}>Cancel</button>
                <button
                  onClick={saveDiscount}
                  disabled={saving}
                  data-testid="discount-save-btn"
                  style={{ flex: 1, padding: '10px 14px', background: '#0F172A', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                >
                  {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Apply
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== ITEM NOTES MODAL ===== */}
      {notesDraftIdx != null && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60 }} onClick={() => setNotesDraftIdx(null)} data-testid="notes-modal">
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', width: 520, borderRadius: 10, overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ padding: '18px 22px', borderBottom: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: 16, fontWeight: 600 }}>Edit Decoration Notes</div>
              <button onClick={() => setNotesDraftIdx(null)} style={{ background: 'transparent', border: 'none', padding: 4, cursor: 'pointer' }}>
                <X size={18} color="#64748B" />
              </button>
            </div>
            <div style={{ padding: 22 }}>
              <textarea
                autoFocus
                value={notesDraft}
                onChange={(e) => setNotesDraft(e.target.value)}
                rows={7}
                data-testid="notes-textarea"
                placeholder="Screen Printing&#10;REORDER of 1012884&#10;40 black shirts provided by client&#10;White front print: Rejuvaline Medspa logo, 6 inches wide"
                style={{ width: '100%', padding: '12px 14px', border: '1px solid #CBD5E1', borderRadius: 6, fontSize: 14, color: '#0F172A', fontFamily: 'inherit', resize: 'vertical', lineHeight: 1.5 }}
              />
              <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                <button onClick={() => setNotesDraftIdx(null)} style={{ flex: 1, padding: '10px 14px', background: '#fff', border: '1px solid #E2E8F0', borderRadius: 6, cursor: 'pointer', fontSize: 13, color: '#475569', fontWeight: 500 }}>Cancel</button>
                <button
                  onClick={saveItemNotes}
                  disabled={saving}
                  data-testid="notes-save-btn"
                  style={{ flex: 1, padding: '10px 14px', background: '#0F172A', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                >
                  {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* ===== ITEM EDITOR MODAL (sizes / price / setup fee / color) ===== */}
      {editItemIdx != null && proposal.items?.[editItemIdx] && (
        <EditProductModal
          proposalId={id}
          itemIndex={editItemIdx}
          item={proposal.items[editItemIdx]}
          taxRate={Number(proposal.tax_rate) || 0}
          onClose={() => setEditItemIdx(null)}
          onSaved={async () => {
            await fetchProposal();
            setEditItemIdx(null);
          }}
        />
      )}

      {/* ===== PRODUCT PICKER (SanMar catalog) ===== */}
      <ProductPickerModal
        open={pickerOpen}
        onClose={() => { setPickerOpen(false); setPickerItemIdx(null); }}
        onPick={handleProductPicked}
      />

      {/* ===== PRODUCT DESIGNER (overlay logo on garment photo) ===== */}
      <ProductDesignerModal
        open={designerItemIdx != null}
        item={designerItemIdx != null ? proposal.items?.[designerItemIdx] : null}
        onClose={() => !designerSaving && setDesignerItemIdx(null)}
        onSave={handleDesignSaved}
        saving={designerSaving}
      />

      {/* ===== FEES MODAL (global setup_fees + additional_fees) ===== */}
      {showFeesModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60 }} onClick={() => !saving && setShowFeesModal(false)} data-testid="fees-modal">
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', width: 440, borderRadius: 10, overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ padding: '18px 22px', borderBottom: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: 16, fontWeight: 600 }}>Edit Fees</div>
              <button onClick={() => setShowFeesModal(false)} style={{ background: 'transparent', border: 'none', padding: 4, cursor: 'pointer' }}>
                <X size={18} color="#64748B" />
              </button>
            </div>
            <div style={{ padding: 22 }}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#64748B', marginBottom: 8 }}>Setup Fees ($)</label>
                <input
                  type="number" min="0" step="0.01"
                  value={feesDraft.setup_fees}
                  onChange={(e) => setFeesDraft((s) => ({ ...s, setup_fees: e.target.value }))}
                  data-testid="fees-setup-input"
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #CBD5E1', borderRadius: 6, fontSize: 15, color: '#0F172A' }}
                />
                <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 6 }}>One-time screen/embroidery digitization fees.</div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#64748B', marginBottom: 8 }}>Additional Fees ($)</label>
                <input
                  type="number" min="0" step="0.01"
                  value={feesDraft.additional_fees}
                  onChange={(e) => setFeesDraft((s) => ({ ...s, additional_fees: e.target.value }))}
                  data-testid="fees-additional-input"
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #CBD5E1', borderRadius: 6, fontSize: 15, color: '#0F172A' }}
                />
                <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 6 }}>Rush charges, art recreations, shipping, etc.</div>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 22 }}>
                <button onClick={() => setShowFeesModal(false)} style={{ flex: 1, padding: '10px 14px', background: '#fff', border: '1px solid #E2E8F0', borderRadius: 6, cursor: 'pointer', fontSize: 13, color: '#475569', fontWeight: 500 }}>Cancel</button>
                <button
                  onClick={saveFees}
                  disabled={saving}
                  data-testid="fees-save-btn"
                  style={{ flex: 1, padding: '10px 14px', background: '#0F172A', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                >
                  {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== STATUS CHANGE CONFIRMATION MODAL ===== */}
      {statusConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60 }} onClick={() => !statusSaving && setStatusConfirm(null)} data-testid="status-confirm-modal">
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', width: 440, borderRadius: 10, overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ padding: '18px 22px', borderBottom: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: 16, fontWeight: 600 }}>Mark as {statusConfirm.label}?</div>
              <button onClick={() => setStatusConfirm(null)} style={{ background: 'transparent', border: 'none', padding: 4, cursor: 'pointer' }}>
                <X size={18} color="#64748B" />
              </button>
            </div>
            <div style={{ padding: 22 }}>
              <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.55, marginBottom: 14 }}>
                {statusConfirm.hint}
              </div>
              <div style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 6, padding: 14, fontSize: 13, color: '#475569' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <Mail size={14} color="#2563EB" />
                  <span style={{ fontWeight: 600, color: '#0F172A' }}>Auto-email will be sent to</span>
                </div>
                <div style={{ paddingLeft: 22, color: '#0F172A' }}>
                  {proposal.customer_email}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
                <button
                  onClick={() => applyStatusChange(statusConfirm.key, false)}
                  disabled={statusSaving}
                  data-testid="status-confirm-no-email-btn"
                  style={{ flex: 1, padding: '10px 14px', background: '#fff', border: '1px solid #E2E8F0', borderRadius: 6, cursor: 'pointer', fontSize: 13, color: '#475569', fontWeight: 500 }}
                >
                  Skip Email
                </button>
                <button
                  onClick={() => applyStatusChange(statusConfirm.key, true)}
                  disabled={statusSaving}
                  data-testid="status-confirm-send-btn"
                  style={{ flex: 1.4, padding: '10px 14px', background: '#0F172A', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                >
                  {statusSaving ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Update & Send Email
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== PICKUP SIGNATURE CAPTURE ===== */}
      <PickupSignatureModal
        open={pickupOpen}
        onClose={() => setPickupOpen(false)}
        onSubmit={handlePickupSubmit}
        saving={pickupSaving}
        defaultName={proposal.customer_name || ''}
      />
    </div>
  );
};

export default ProposalViewV2;
